
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 5,
  layout: 'guide',
  description: 'Guide to Hosted Link for embedded clients'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Hosted Link for embedded clients' subtitle='Hosted Link integration for PSPs and embedded clients' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Some customers may not have apps themselves, but instead are embedded within other apps. For example, a payment service provider (PSP) may build a checkout flow which provides several payment options, one of which is pay by bank powered by Plaid. This checkout flow is then used by the PSP's customers within their apps. For these embedded clients, Hosted Link is the recommended approach. This guide will cover implementing `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link"
      }}>{`Hosted Link`}</a>{` specifically for the embedded client use case, covering integration aspects that are unique to this scenario. `}</p>
    <Callout mdxType="Callout">
      <p>{`While not all customers who use the embedded flow are necessarily PSPs, for the sake of readability, this guide will refer to the client that is embedded within a third-party app as the PSP, and the app that they are embedded within as the merchant.`}</p>
    </Callout>
    <p>{`Hosted Link has the following benefits for PSPs:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Simplicity:`}</strong>{` Hosted Link is the easiest way to integrate with Plaid when Plaid's native mobile SDKs cannot be used. `}</li>
      <li parentName="ul"><strong parentName="li">{`Continuity:`}</strong>{` With Hosted Link, Plaid manages the redirects between Plaid Link and the bank's OAuth site. Configuring redirects to a merchant app may not be possible for some PSPs, so Hosted Link ensures the Link session can be completed wherever it is opened. `}</li>
      <li parentName="ul"><strong parentName="li">{`Easier access to Link callbacks and events:`}</strong>{` When Link is launched via Plaid SDKs, Link events and metadata, including the public token, are typically returned via frontend callbacks. With Hosted Link, Link events are returned via backend API calls, making it easier to access information about the Link session without owning the frontend experience. `}</li>
    </ul>
    <h4 {...{
      "id": "overview-of-hosted-link-flow-for-embedded-clients"
    }}>{`Overview of Hosted Link flow for embedded clients`}</h4>
    <h5 {...{
      "id": "in-app-embeds"
    }}>{`In-app embeds`}</h5>
    <p>{`Hosted Link URLs are required to be opened in a secure context such as an `}<inlineCode parentName="p">{`ASWebAuthenticationSession`}</inlineCode>{` on iOS or a Chrome Custom Tab on Android. Setting `}<inlineCode parentName="p">{`hosted_link.is_mobile_app`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` will indicate to Plaid that Hosted Link is being opened in a secure webview, so that Plaid will use the correct redirects.`}</p>
    <h5 {...{
      "id": "webpage-embeds"
    }}>{`Webpage embeds`}</h5>
    <p>{`If the flow is embedded in a webpage, there are two options for handling the Hosted Link URI.`}</p>
    <h6 {...{
      "id": "redirecting-to-hosted-link"
    }}>{`Redirecting to Hosted Link`}</h6>
    <p>{`The merchant can redirect their entire page to the Plaid Hosted Link URI. Upon the user completing Link, Plaid will redirect that webpage to the `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` provided in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call.`}</p>
    <h6 {...{
      "id": "opening-hosted-link-in-a-new-window-or-tab"
    }}>{`Opening Hosted Link in a new window or tab`}</h6>
    <p>{`The merchant can open the Plaid Hosted Link URI in a new tab (for mobile web), or pop-up (for desktop web). This allows the user to link with Plaid while keeping the context of the checkout flow behind the pop-up. Upon completion, Plaid can close this pop-up / new tab, bringing the user back to the merchant checkout flow.`}</p>
    <h4 {...{
      "id": "hosted-link-integration-modes"
    }}>{`Hosted Link integration modes`}</h4>
    <p>{`There are two main types of flows that an embedded app may use. The instructions for integrating Hosted Link will be different for each type, so to integrate with Hosted Link, you will need to identify which one your app uses.`}</p>
    <h5 {...{
      "id": "with-intermediary-steps"
    }}>{`With intermediary steps`}</h5>
    <p>{`In the first type of flow, with intermediary steps, an end user is taken from the merchant to an intermediary page that is controlled directly by the PSP, where they will typically pick a payment method, complete the payment, and then be returned to the intermediary page, which will redirect the end user back to the merchant.  `}</p>
    <p>{`This flow provides slightly more complexity for the end customer, but does not require any additional integration work from the merchant. `}</p>
    <h5 {...{
      "id": "without-intermediary-steps"
    }}>{`Without intermediary steps`}</h5>
    <p>{`In the second type of flow, without intermediary steps, the end user never goes to a page controlled by the PSP. Instead, they will go directly from the merchant's app to the payment method, then return to the merchant's app. `}</p>
    <p>{`This flow is simpler from the end customer's perspective, but may require additional integration steps by the merchant.`}</p>
    <h4 {...{
      "id": "integration-process-with-intermediary-steps"
    }}>{`Integration process with intermediary steps`}</h4>
    <p>{`Follow the instructions for using `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link"
      }}>{`Hosted Link`}</a>{`. In the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call, the `}<inlineCode parentName="p">{`hosted_link.completion_redirect_uri`}</inlineCode>{` should be set to your website, which will then handle the process of redirecting the user back to the merchant page.`}</p>
    <h4 {...{
      "id": "integration-process-without-intermediary-steps"
    }}>{`Integration process without intermediary steps`}</h4>
    <p>{`Follow the instructions for using `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link"
      }}>{`Hosted Link`}</a>{`, and see the sections below for instructions specific to the embedded client flow.`}</p>
    <h5 {...{
      "id": "completion-redirect-uris"
    }}>{`Completion redirect URIs`}</h5>
    <p>{`If you would like Plaid to redirect directly to the merchant app, you will use a separate `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` for each merchant.`}</p>
    <p>{`As an alternative, you may want the `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` to be a single URL that you host yourself, and manage the redirects back to merchant apps independently of Plaid.`}</p>
    <p>{` If no `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` is provided, the user will see a "Return to app" message on a Plaid.com domain in the browser and must manually navigate back to the merchant app.`}</p>
    <h6 {...{
      "id": "merchant-integration-steps"
    }}>{`Merchant integration steps`}</h6>
    <p>{`Because the application controls how URLs are handled, the merchant may need to add code to their app to open the Plaid hosted Link within the default browser, especially if the PSP's page is opened from within a webview.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-examples/blob/master/webviews/android/LinkWebview/app/src/main/java/com/example/linkwebview/MainActivity.kt#L94-L176"
      }}>{`Example function for Android`}</a>{` `}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-examples/blob/master/webviews/wkwebview/wkwebview/LinkViewController.swift#L56-L72"
      }}>{`Example function for iOS`}</a></p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "overview-of-hosted-link-flow-for-embedded-clients",
        level: 4,
        title: "Overview of Hosted Link flow for embedded clients",
      },
{
        id: "hosted-link-integration-modes",
        level: 4,
        title: "Hosted Link integration modes",
      },
{
        id: "integration-process-with-intermediary-steps",
        level: 4,
        title: "Integration process with intermediary steps",
      },
{
        id: "integration-process-without-intermediary-steps",
        level: 4,
        title: "Integration process without intermediary steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
